import { useAuth, useConfirmation, useServices, useToast } from '@/app/providers';
import { Badge, Button, Card, StatsCard } from '@/core/ui/components';
import type { TenantDashboard } from '@yawi/types';
import {
  Activity,
  ArrowLeft,
  Building2,
  CalendarCheck,
  CreditCard,
  DollarSign,
  DoorOpen,
  Edit,
  ExternalLink,
  Loader2,
  LogIn,
  Pause,
  Play,
  Settings,
  Ticket,
  Upload,
  Users,
  KeyRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdminsListCard,
  AccessLevelSelector,
  HikCentralVisitorGroupConfig,
  ApiKeysCard,
  BillingCard,
  BulkImportTab,
  HikCentralImportTab,
  HikCentralSuperConfigTab,
  ImpersonateModal,
  IntegrationsCard,
  SubscriptionCard,
  TenantActivityLog,
  TenantFormModal,
  TenantQuotas,
  UsageCard,
  WebhooksCard,
} from '../components';
import { TenantBillingOverview } from '@/modules/billing/ui/components/tenant';
import { AccessDoorsTab } from '@/modules/access-doors';

type TabId = 'overview' | 'subscription' | 'billing' | 'team' | 'technical' | 'activity' | 'import' | 'hikcentral' | 'doors';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'General', icon: <Building2 className="w-4 h-4" /> },
  { id: 'subscription', label: 'Suscripción', icon: <CreditCard className="w-4 h-4" /> },
  { id: 'billing', label: 'Billing', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'team', label: 'Equipo', icon: <Users className="w-4 h-4" /> },
  { id: 'technical', label: 'Técnico', icon: <Settings className="w-4 h-4" /> },
  { id: 'activity', label: 'Actividad', icon: <Activity className="w-4 h-4" /> },
  { id: 'import', label: 'Importación', icon: <Upload className="w-4 h-4" /> },
  { id: 'hikcentral', label: 'HikCentral', icon: <KeyRound className="w-4 h-4" /> },
  { id: 'doors', label: 'Puertas y Cámaras', icon: <DoorOpen className="w-4 h-4" /> },
];

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  ACTIVE: { variant: 'success', label: 'Activo' },
  active: { variant: 'success', label: 'Activo' },
  SUSPENDED: { variant: 'warning', label: 'Suspendido' },
  suspended: { variant: 'warning', label: 'Suspendido' },
  PENDING: { variant: 'default', label: 'Pendiente' },
  pending: { variant: 'default', label: 'Pendiente' },
  CANCELLED: { variant: 'error', label: 'Cancelado' },
  cancelled: { variant: 'error', label: 'Cancelado' },
  PENDING_SETUP: { variant: 'default', label: 'Pendiente de configurar' },
  DELETED: { variant: 'error', label: 'Eliminado' },
};

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenantRepository } = useServices();
  const { showToast } = useToast();
  const { confirm } = useConfirmation();
  const { accessTenant } = useAuth();

  const [dashboard, setDashboard] = useState<TenantDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  useEffect(() => {
    if (!id) return;

    const fetchDashboard = async () => {
      setIsLoading(true);
      try {
        const response = await tenantRepository.getDashboard(id);
        setDashboard(response);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error al cargar el tenant';
        showToast('error', message);
        navigate('/tenants');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [id, tenantRepository, showToast, navigate]);

  const handleSuspend = async () => {
    if (!dashboard) return;

    const confirmed = await confirm({
      title: 'Suspender Tenant',
      message: `¿Estás seguro de que deseas suspender "${dashboard.tenant.name}"? Los usuarios no podrán acceder a la plataforma.`,
      variant: 'warning',
      confirmText: 'Suspender',
    });

    if (confirmed) {
      try {
        const response = await tenantRepository.suspend(dashboard.tenant.id);
        setDashboard((prev) => prev ? { ...prev, tenant: { ...prev.tenant, status: response.status } } : null);
        showToast('success', 'Tenant suspendido correctamente');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error al suspender tenant';
        showToast('error', message);
      }
    }
  };

  const handleActivate = async () => {
    if (!dashboard) return;

    try {
      const response = await tenantRepository.activate(dashboard.tenant.id);
      setDashboard((prev) => prev ? { ...prev, tenant: { ...prev.tenant, status: response.status } } : null);
      showToast('success', 'Tenant activado correctamente');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al activar tenant';
      showToast('error', message);
    }
  };

  const handleAccessTenant = async () => {
    if (!dashboard) return;

    try {
      await accessTenant(dashboard.tenant.id);
      window.open(`https://${dashboard.tenant.customDomain || `${dashboard.tenant.slug}.hommi.mx`}`, '_blank');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'No se pudo acceder al tenant');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  const { tenant, stats } = dashboard;
  const badge = STATUS_BADGES[tenant.status] || STATUS_BADGES.PENDING;
  const isActive = tenant.status === 'ACTIVE' || tenant.status === 'active';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/tenants')}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-theme-secondary" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-theme">{tenant.name}</h1>
                <Badge variant={badge.variant}>{badge.label}</Badge>
              </div>
              <p className="text-theme-secondary">{tenant.customDomain || `${tenant.slug}.hommi.mx`}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            leftIcon={<ExternalLink className="w-4 h-4" />}
            onClick={handleAccessTenant}
          >
            Acceder
          </Button>
          <Button
            variant="outline"
            leftIcon={<LogIn className="w-4 h-4" />}
            onClick={() => setShowImpersonateModal(true)}
          >
            Impersonar
          </Button>
          <Button
            variant="outline"
            leftIcon={<Edit className="w-4 h-4" />}
            onClick={() => setShowEditModal(true)}
          >
            Editar
          </Button>
          {isActive ? (
            <Button
              variant="outline"
              leftIcon={<Pause className="w-4 h-4" />}
              onClick={handleSuspend}
              className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            >
              Suspender
            </Button>
          ) : tenant.status === 'DELETED' ? null : (
            <Button
              variant="outline"
              leftIcon={<Play className="w-4 h-4" />}
              onClick={handleActivate}
              className="text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
            >
              Activar
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Pagos este mes"
          value={stats?.payments?.paymentsThisMonth ?? 0}
          icon={<DollarSign className="w-6 h-6 text-green-600" />}
        />
        <StatsCard
          title="Miembros activos"
          value={stats?.activeMembers ?? 0}
          icon={<Users className="w-6 h-6 text-blue-600" />}
        />
        <StatsCard
          title="Tickets abiertos"
          value={stats?.tickets?.open ?? 0}
          icon={<Ticket className="w-6 h-6 text-amber-600" />}
        />
        <StatsCard
          title="Reservaciones activas"
          value={stats?.amenities?.activeReservations ?? 0}
          icon={<CalendarCheck className="w-6 h-6 text-purple-600" />}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-theme-secondary hover:text-theme hover:border-slate-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* General Info */}
            <Card>
              <h3 className="text-lg font-semibold text-theme mb-4">Información General</h3>
              <dl className="space-y-4">
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">ID</dt>
                  <dd className="text-theme font-mono text-sm">{tenant.id}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Slug</dt>
                  <dd className="text-theme">{tenant.slug}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Plan</dt>
                  <dd><Badge variant="purple">{dashboard.subscription?.planName ?? tenant.plan}</Badge></dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Estado</dt>
                  <dd><Badge variant={badge.variant}>{badge.label}</Badge></dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Creado</dt>
                  <dd className="text-theme">{new Date(tenant.createdAt).toLocaleString()}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Última actualización</dt>
                  <dd className="text-theme">{new Date(tenant.updatedAt).toLocaleString()}</dd>
                </div>
              </dl>
            </Card>

            {/* Settings */}
            <Card>
              <h3 className="text-lg font-semibold text-theme mb-4">Configuración</h3>
              <dl className="space-y-4">
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Zona horaria</dt>
                  <dd className="text-theme">{tenant.settings?.timezone || 'No configurada'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Idioma</dt>
                  <dd className="text-theme">{tenant.settings?.language || 'No configurado'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-theme-secondary">Moneda</dt>
                  <dd className="text-theme">{tenant.settings?.currency || 'No configurada'}</dd>
                </div>
              </dl>
            </Card>

            {/* Stats Summary */}
            <Card>
              <h3 className="text-lg font-semibold text-theme mb-4">Resumen</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-sm text-theme-secondary">Unidades</p>
                  <p className="text-xl font-semibold text-theme">
                    {stats?.occupiedUnits ?? 0} / {stats?.totalUnits ?? 0}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-sm text-theme-secondary">Vehículos</p>
                  <p className="text-xl font-semibold text-theme">{stats?.vehicles ?? 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-sm text-theme-secondary">Mascotas</p>
                  <p className="text-xl font-semibold text-theme">{stats?.pets ?? 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-sm text-theme-secondary">Comunicados</p>
                  <p className="text-xl font-semibold text-theme">{stats?.announcements ?? 0}</p>
                </div>
              </div>
            </Card>

            {/* Quotas */}
            <TenantQuotas tenantId={tenant.id} />
          </div>
        )}

        {activeTab === 'subscription' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SubscriptionCard subscription={dashboard.subscription} />
            <BillingCard billing={dashboard.billing} />
            <div className="lg:col-span-2">
              <UsageCard usage={dashboard.usage} />
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <TenantBillingOverview tenantId={tenant.id} />
        )}

        {activeTab === 'team' && (
          <div className="max-w-3xl">
            <AdminsListCard admins={dashboard.admins} />
          </div>
        )}

        {activeTab === 'technical' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ApiKeysCard apiKeys={dashboard.apiKeys} />
            <WebhooksCard webhooks={dashboard.webhooks} />
            <div className="lg:col-span-2">
              <IntegrationsCard integrations={dashboard.integrations} />
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="max-w-3xl">
            <TenantActivityLog tenantId={tenant.id} />
          </div>
        )}

        {activeTab === 'import' && (
          <BulkImportTab tenantId={tenant.id} />
        )}

        {activeTab === 'hikcentral' && (() => {
          const hikIntegration = dashboard.integrations.find((i) => i.provider === 'HIKCENTRAL');
          return (
            <div className="space-y-6">
              {hikIntegration ? (
                <HikCentralSuperConfigTab
                  tenantId={tenant.id}
                  integrationId={hikIntegration.id}
                />
              ) : (
                <div className="text-sm text-theme-secondary p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  No hay una integración HikCentral configurada para este tenant.
                </div>
              )}
              <HikCentralImportTab tenantId={tenant.id} />
              <HikCentralVisitorGroupConfig tenantId={tenant.id} />
              <AccessLevelSelector tenantId={tenant.id} />
            </div>
          );
        })()}

        {activeTab === 'doors' && (
          <AccessDoorsTab tenantId={tenant.id} />
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <TenantFormModal
          tenant={tenant}
          planName={dashboard.subscription?.planName ?? null}
          onClose={() => setShowEditModal(false)}
          onSuccess={async () => {
            setShowEditModal(false);
            // Relectura: confirma lo que quedó persistido, no lo que se envió
            try {
              setDashboard(await tenantRepository.getDashboard(tenant.id));
            } catch (err) {
              showToast('error', err instanceof Error ? err.message : 'Error al recargar el tenant');
            }
          }}
        />
      )}

      {/* Impersonate Modal */}
      {showImpersonateModal && (
        <ImpersonateModal
          tenant={tenant}
          admins={dashboard.admins}
          onClose={() => setShowImpersonateModal(false)}
        />
      )}
    </div>
  );
}
